import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, Menu, Monitor, Moon, Sun, X } from 'lucide-react';
import SiameseAILogoIcon from '../icons/SiameseAILogoIcon';

const CONTACT_EMAIL = 'hello@siameseai.com';
const CONTACT_SUBJECT = 'Hello SiameseAI';
const MAILTO_HREF = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(CONTACT_SUBJECT)}`;

const NAV_LINKS = [
  { href: '#thesis', label: 'Thesis' },
  { href: '#blackstable', label: 'BlackStable' },
  { href: '#team', label: 'Team' },
];

const THEME_OPTIONS = [
  { id: 'system', label: 'System', Icon: Monitor },
  { id: 'light', label: 'Light', Icon: Sun },
  { id: 'dark', label: 'Dark', Icon: Moon },
];

function ThemeMenu({ themePreference, onSetThemePreference }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const activeOption =
    THEME_OPTIONS.find((option) => option.id === themePreference) || THEME_OPTIONS[0];
  const ActiveIcon = activeOption.Icon;

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleSelect = useCallback(
    (id) => {
      onSetThemePreference(id);
      setOpen(false);
    },
    [onSetThemePreference],
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={`Theme: ${activeOption.label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
      >
        <ActiveIcon size={18} strokeWidth={1.6} />
      </button>
      <div
        role="menu"
        aria-label="Theme"
        className={`absolute right-0 mt-2 w-40 origin-top-right rounded-md border border-border bg-card/95 backdrop-blur-md shadow-2xl shadow-primary/10 p-1 z-50 transition-all duration-150 ${
          open
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-1 pointer-events-none'
        }`}
      >
        {THEME_OPTIONS.map(({ id, label, Icon }) => {
          const isActive = id === themePreference;
          return (
            <button
              key={id}
              type="button"
              role="menuitemradio"
              aria-checked={isActive}
              onClick={() => handleSelect(id)}
              className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-sm text-sm transition-colors ${
                isActive
                  ? 'text-foreground bg-secondary/60'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'
              }`}
            >
              <Icon size={14} strokeWidth={1.6} />
              <span className="flex-1 text-left">{label}</span>
              {isActive ? (
                <Check size={12} strokeWidth={2} className="text-primary" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MobileMenu({ open, onClose, themePreference, onSetThemePreference }) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  return (
    <div
      ref={panelRef}
      className={`sm:hidden overflow-hidden border-t border-border/60 bg-background/95 backdrop-blur-md transition-[max-height,opacity] duration-200 ease-out ${
        open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
      }`}
      aria-hidden={!open}
    >
      <div className="px-6 py-5 space-y-5">
        <ul className="divide-y divide-border/60 border-y border-border/60">
          {NAV_LINKS.map(({ href, label }) => (
            <li key={href}>
              <a
                href={href}
                onClick={onClose}
                className="block py-3 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors"
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
        <div
          role="radiogroup"
          aria-label="Theme"
          className="grid grid-cols-3 gap-1 p-1 rounded-md border border-border bg-card/60"
        >
          {THEME_OPTIONS.map(({ id, label, Icon }) => {
            const isActive = id === themePreference;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => onSetThemePreference(id)}
                className={`flex flex-col items-center justify-center gap-1 py-2 rounded-sm text-xs transition-colors ${
                  isActive
                    ? 'text-foreground bg-secondary/70'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'
                }`}
              >
                <Icon size={16} strokeWidth={1.6} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
        <a
          href={MAILTO_HREF}
          onClick={onClose}
          className="cta-button w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded text-xs"
        >
          Get in touch
          <ArrowRight size={14} strokeWidth={1.8} />
        </a>
      </div>
    </div>
  );
}

function TopBar({ themePreference, onSetThemePreference }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef(null);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const handlePointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [mobileOpen]);

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-50 w-full backdrop-blur-md bg-background/70 border-b border-border/60"
    >
      <div className="mx-auto max-w-6xl px-6 sm:px-8 h-16 flex items-center justify-between">
        <a
          href="#top"
          className="flex items-center group hover:opacity-80 transition-opacity"
          aria-label="SiameseAI home"
          onClick={closeMobile}
        >
          <SiameseAILogoIcon size={28} />
        </a>

        <nav aria-label="Primary" className="hidden sm:flex items-center gap-3">
          <ul className="flex items-center gap-6 mr-3">
            {NAV_LINKS.map(({ href, label }) => (
              <li key={href}>
                <a
                  href={href}
                  className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
          <ThemeMenu
            themePreference={themePreference}
            onSetThemePreference={onSetThemePreference}
          />
          <a
            href={MAILTO_HREF}
            className="cta-button inline-flex items-center gap-1 px-2.5 py-1 rounded text-[0.5rem] tracking-[0.12em]"
          >
            Get in touch
            <ArrowRight size={10} strokeWidth={1.8} />
          </a>
        </nav>

        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          aria-controls="topbar-mobile-menu"
          className="sm:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
        >
          {mobileOpen ? (
            <X size={20} strokeWidth={1.6} />
          ) : (
            <Menu size={20} strokeWidth={1.6} />
          )}
        </button>
      </div>

      <div id="topbar-mobile-menu">
        <MobileMenu
          open={mobileOpen}
          onClose={closeMobile}
          themePreference={themePreference}
          onSetThemePreference={onSetThemePreference}
        />
      </div>
    </header>
  );
}

export default TopBar;
